import { useEffect, useState, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

// Configure axios defaults for better network handling
axios.defaults.timeout = 30000; // 30 second timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add axios request interceptor to always include token from localStorage
axios.interceptors.request.use(
  (config) => {
    // If no Authorization header is set, try to get token from localStorage
    if (!config.headers.Authorization) {
      const token = localStorage.getItem("revurge_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add axios response interceptor for better error handling and retries
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Don't retry if already retried or if it's a 401/403 (auth error)
    if (originalRequest._retry || error.response?.status === 401 || error.response?.status === 403) {
      return Promise.reject(error);
    }
    
    // Retry on network errors or 5xx errors (up to 2 retries)
    if (!error.response || (error.response.status >= 500 && error.response.status < 600)) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      
      if (originalRequest._retryCount <= 2) {
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 500 * originalRequest._retryCount));
        return axios(originalRequest);
      }
    }
    
    return Promise.reject(error);
  }
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Loading Spinner Component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-[#e570d8] border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

// Lazy load pages for code splitting
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LandingPageLight = lazy(() => import("./pages/LandingPageLight"));
const ProcessPage = lazy(() => import("./pages/ProcessPage"));
const VentureStudioForCreators = lazy(() => import("./pages/VentureStudioForCreators"));
const SEOPage = lazy(() => import("./pages/SEOPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const BrandStudio = lazy(() => import("./pages/BrandStudio"));
const ManufacturerHub = lazy(() => import("./pages/ManufacturerHub"));
const ProductionTracker = lazy(() => import("./pages/ProductionTracker"));
const LaunchCenter = lazy(() => import("./pages/LaunchCenter"));
const TrendsExplorer = lazy(() => import("./pages/TrendsExplorer"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminFactories = lazy(() => import("./pages/AdminFactories"));
const AdminInsights = lazy(() => import("./pages/AdminInsights"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminMaster = lazy(() => import("./pages/AdminMaster"));
const TeamDashboard = lazy(() => import("./pages/TeamDashboard"));
const FactoryDashboard = lazy(() => import("./pages/FactoryDashboard"));
const BrandStatusScreen = lazy(() => import("./pages/BrandStatusScreen"));
const CostCalculator = lazy(() => import("./pages/CostCalculator"));
const Messages = lazy(() => import("./pages/Messages"));
const SampleRequests = lazy(() => import("./pages/SampleRequests"));
const SuccessPlaybooks = lazy(() => import("./pages/SuccessPlaybooks"));
const LiveProductionTracker = lazy(() => import("./pages/LiveProductionTracker"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ConnectedStores = lazy(() => import("./pages/ConnectedStores"));
const SyncedProducts = lazy(() => import("./pages/SyncedProducts"));
const StoreAnalytics = lazy(() => import("./pages/StoreAnalytics"));
const ProductionCenter = lazy(() => import("./pages/ProductionCenter"));
const CreatePurchaseOrder = lazy(() => import("./pages/CreatePurchaseOrder"));
const PurchaseOrderDetail = lazy(() => import("./pages/PurchaseOrderDetail"));
const BrandIntelligence = lazy(() => import("./pages/BrandIntelligence"));
const UseCasesPage = lazy(() => import("./pages/UseCasesPage"));
const BrowseCategoriesPage = lazy(() => import("./pages/BrowseCategoriesPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Resolve a brand/saved logo URL: absolute & data URLs pass through; relative API
// paths (e.g. "/api/brands/{id}/logo") are prefixed with the backend origin.
export const resolveLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("revurge_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem("revurge_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        let lastError = null;
        // Retry transient failures so a slow/blip response never logs the user out
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const response = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 15000
            });
            setUser(response.data);
            localStorage.setItem("revurge_user", JSON.stringify(response.data));
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            const status = error.response?.status;
            // Only a genuinely invalid/expired token should end the session
            if (status === 401 || status === 403) {
              localStorage.removeItem("revurge_token");
              localStorage.removeItem("revurge_user");
              setToken(null);
              setUser(null);
              lastError = null;
              break;
            }
            // Transient (timeout / network / 5xx): wait and retry
            await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
          }
        }
        // If all retries failed on a transient error, KEEP the session
        // (token + cached user stay intact) so a refresh recovers gracefully.
        if (lastError) {
          const cached = localStorage.getItem("revurge_user");
          if (cached) {
            try { setUser(JSON.parse(cached)); } catch { /* noop */ }
          }
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem("revurge_token", access_token);
    localStorage.setItem("revurge_user", JSON.stringify(user));
    setToken(access_token);
    setUser(user);
    return user;
  };

  const register = async (email, password, full_name, role = "brand_owner") => {
    const response = await axios.post(`${API}/auth/register`, { email, password, full_name, role });
    const { access_token, user } = response.data;
    localStorage.setItem("revurge_token", access_token);
    localStorage.setItem("revurge_user", JSON.stringify(user));
    setToken(access_token);
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem("revurge_token");
    localStorage.removeItem("revurge_user");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="noise-bg">
          <Toaster 
            position="top-center" 
            richColors 
            closeButton 
            theme="light"
            toastOptions={{
              duration: 4500,
              className: "revurge-toast",
              style: {
                fontFamily: "'Manrope', sans-serif",
                borderRadius: "16px",
                borderWidth: "1px",
                boxShadow: "0 14px 34px -10px rgba(229,112,216,0.28)",
              },
            }}
          />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPageLight />} />
              <Route path="/process" element={<ProcessPage />} />
              <Route path="/venture-studio-for-creators" element={<VentureStudioForCreators />} />
              <Route path="/dark" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* User Routes */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/brand-studio" element={<ProtectedRoute><BrandStudio /></ProtectedRoute>} />
              <Route path="/brand-studio/:brandId" element={<ProtectedRoute><BrandStudio /></ProtectedRoute>} />
              <Route path="/manufacturers" element={<ProtectedRoute><ManufacturerHub /></ProtectedRoute>} />
              <Route path="/production/:brandId" element={<ProtectedRoute><ProductionTracker /></ProtectedRoute>} />
              <Route path="/brand-status/:brandId" element={<ProtectedRoute><BrandStatusScreen /></ProtectedRoute>} />
              <Route path="/launch/:brandId" element={<ProtectedRoute><LaunchCenter /></ProtectedRoute>} />
              <Route path="/trends" element={<ProtectedRoute><TrendsExplorer /></ProtectedRoute>} />
              <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/master" element={<ProtectedRoute allowedRoles={["super_admin", "ops_team"]}><AdminMaster /></ProtectedRoute>} />
              <Route path="/admin/factories" element={<ProtectedRoute allowedRoles={["super_admin", "procurement_team", "ops_team", "packaging_team", "branding_team", "business_team"]}><AdminFactories /></ProtectedRoute>} />
              <Route path="/admin/insights" element={<ProtectedRoute allowedRoles={["super_admin", "ops_team"]}><AdminInsights /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminUsers /></ProtectedRoute>} />
              
              {/* Team Routes */}
              <Route path="/team" element={<ProtectedRoute allowedRoles={["ops_team", "packaging_team", "procurement_team"]}><TeamDashboard /></ProtectedRoute>} />
              
              {/* Factory Routes */}
              <Route path="/factory-dashboard" element={<ProtectedRoute allowedRoles={["factory"]}><FactoryDashboard /></ProtectedRoute>} />
              
              {/* New Feature Routes - Phase 1-4 */}
              <Route path="/cost-calculator" element={<ProtectedRoute><CostCalculator /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/messages/:conversationId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/sample-requests" element={<ProtectedRoute><SampleRequests /></ProtectedRoute>} />
              <Route path="/playbooks" element={<ProtectedRoute><SuccessPlaybooks /></ProtectedRoute>} />
              <Route path="/live-production/:brandId" element={<ProtectedRoute><LiveProductionTracker /></ProtectedRoute>} />
              
              {/* Operations Routes (Shopify Sync) */}
              <Route path="/operations/stores" element={<ProtectedRoute><ConnectedStores /></ProtectedRoute>} />
              <Route path="/operations/products/:storeId" element={<ProtectedRoute><SyncedProducts /></ProtectedRoute>} />
              <Route path="/operations/analytics/:storeId" element={<ProtectedRoute><StoreAnalytics /></ProtectedRoute>} />
              
              {/* Production Command Center Routes */}
              <Route path="/operations/production" element={<ProtectedRoute><ProductionCenter /></ProtectedRoute>} />
              <Route path="/operations/production/new" element={<ProtectedRoute><CreatePurchaseOrder /></ProtectedRoute>} />
              <Route path="/operations/production/:poId" element={<ProtectedRoute><PurchaseOrderDetail /></ProtectedRoute>} />
              
              {/* Brand Intelligence Routes */}
              <Route path="/operations/intelligence" element={<ProtectedRoute><BrandIntelligence /></ProtectedRoute>} />
              <Route path="/operations/intelligence/:storeId" element={<ProtectedRoute><BrandIntelligence /></ProtectedRoute>} />
              
              {/* Use Cases / Case Studies */}
              <Route path="/use-cases" element={<UseCasesPage />} />
              
              {/* Browse Categories - SEO Page Directory */}
              <Route path="/browse-categories" element={<BrowseCategoriesPage />} />

              {/* SEO keyword landing pages (data-driven, /:slug) - keep last so specific routes win */}
              <Route path="/:seoSlug" element={<SEOPage />} />
            </Routes>
          </Suspense>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
